import React, { useEffect, DependencyList } from 'react';
import {
  useApiCall,
  ApiCall,
  ClientConstructor,
  RequestStatus,
  UseApiCallOptions,
} from './useApiCall';

export interface ApiResponse<Response> {
  response: ApiCall<Response, []>['response'];
  error: ApiCall<Response, []>['error'];
  status: ApiCall<Response, []>['status'];
  setStatus: ApiCall<Response, []>['setStatus'];
  update(
    response?: React.SetStateAction<Response | undefined>,
    options?: UseApiCallOptions
  ): Promise<[Response, null] | [null, unknown] | undefined>;
}

/** use deps array to automatically call methodCaller when a dependency changes */
export const useResponse = <Client, Response>(
  client: ClientConstructor<Client>,
  methodCaller: (client: Client) => Promise<Response>,
  deps?: DependencyList,
  options?: UseApiCallOptions
): ApiResponse<Response> => {
  const apiCall = useApiCall(client, methodCaller, deps, {
    ...options,
    initialRequestStatus:
      options?.initialRequestStatus ?? RequestStatus.Fetching,
  });

  useEffect(() => {
    apiCall.run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps || []);

  const update = async (
    response?: React.SetStateAction<Response | undefined>,
    updateOptions?: UseApiCallOptions
  ) => {
    if (response !== undefined) {
      apiCall.setResponse(response);
    } else {
      return await apiCall.advancedRun([], updateOptions ?? options);
    }
  };

  return {
    response: apiCall.response,
    status: apiCall.status,
    error: apiCall.error,
    setStatus: apiCall.setStatus,
    update,
  };
};
